import React, { useMemo, useState, useEffect } from "react";
import AssessmentsTable from "../assessments.table";
import { FIELD_TYPES } from "../constants";
import InitialRiskAssessmentTable from "../initial.risk.assessment.table";
import VisitSampleStrategyTables from "../visit.sample.strategy.tables";
import VisitsInformationTable from "../soe.table";
import VisitsTable from "../visits.table";
import styled from "styled-components";
import { useScrollPositionTracking } from "../hooks/useScrollRestoration";

export default function Content({
  tabs,
  value,
  editOpen,
  setEditOpen,
  siteId,
  fieldType,
  setFieldType,
  setHasChanges,
  updateContext,
  setSaveSOE,
}) {
  // Track scroll position and edited components
  useScrollPositionTracking(value);

  // initial a boolean called showInitialRiskAssessment using the useMemo hook and the value of the fieldType
  const showInitialRiskAssessment = useMemo(() => {
    return fieldType === FIELD_TYPES.MEDICAL;
  }, [fieldType]);

  const showVisitSampleStrategy = useMemo(() => {
    return fieldType === FIELD_TYPES.MEDICAL;
  }, [fieldType]);

  const [tableVisibility, setTableVisibilityState] = useState({
    showVisitTable: true,
    showAssessmentsTable: false,
    showVisitInformationTable: false,
  });

  const setTableVisibility = (tableName) => {
    setTableVisibilityState((prevState) => {
      if (value === 0) {
        const resetState = {
          showVisitTable: false,
          showAssessmentsTable: false,
          showVisitInformationTable: false,
        };
        return { ...resetState, [tableName]: true };
      }

      return {
        ...prevState,
        [tableName]: !prevState[tableName],
      };
    });
  };

  useEffect(() => {
    const isVisible = value !== 0;
    setTableVisibilityState({
      showVisitTable: true,
      showAssessmentsTable: isVisible,
      showVisitInformationTable: isVisible,
    });
  }, [value]);

  // Restore table visibility after save
  useEffect(() => {
    const restoreTableVisibility = (event) => {
      // Get componentName from event detail
      const componentName = event.detail?.componentName;
      
      if (componentName) {
        // Map of table component names to their visibility state keys
        const tableNameMap = {
          // Main tables with visibility state
          'VisitsTable': 'showVisitTable',
          'AssessmentsTable': 'showAssessmentsTable',
          'VisitInformationTable': 'showVisitInformationTable',
          
          // Nested accordions without their own visibility state in content.js
          // They manage their own expansion state internally via useRestoreTableVisibility hook
          'InitialRiskAssessmentTable': null,
          
          // VisitSampleStrategyTables subtables
          'VariablesTable': null,
          'EPriceTable': null,
          'CRATable': null,
          'IMVTable': null,
          'CDATable': null,
          'CDAStudyTable': null,
          'EPriceInfoTable': null,
        };

        const tableName = tableNameMap[componentName];
        
        // If it's a main table with visibility state, update it
        if (tableName) {
          // At study level (value === 0), close all other tables before opening the edited one
          // At country/site level (value !== 0), just ensure the edited table is open
          setTableVisibilityState((prevState) => {
            const newState = value === 0 
              ? {
                  showVisitTable: false,
                  showAssessmentsTable: false,
                  showVisitInformationTable: false,
                  [tableName]: true,
                }
              : {
                  ...prevState,
                  [tableName]: true,
                };
            
            return newState;
          });
        }
      }
    };

    // Listen for custom event to restore visibility
    window.addEventListener('soe-restore-visibility', restoreTableVisibility);
    
    return () => {
      window.removeEventListener('soe-restore-visibility', restoreTableVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return tabs?.map(
    (tab) =>
      tabs[value]?.siteGroupId === tab?.siteGroupId && (
        <TabContainer
          key={tab?.countryCodeISO3}
          value={value}
          tab={tab?.countryCodeISO3}
        >
          <VisitsTable
            editOpen={editOpen}
            setEditOpen={setEditOpen}
            value={value}
            country={tab}
            fieldType={fieldType}
            setFieldType={setFieldType}
            siteId={siteId}
            setTableVisibility={setTableVisibility}
            tableVisibility={tableVisibility}
          />
          <AssessmentsTable
            fieldType={fieldType}
            setFieldType={setFieldType}
            value={value}
            country={tab}
            siteId={siteId}
            setTableVisibility={setTableVisibility}
            tableVisibility={tableVisibility}
          />
          {value === 0 && (
            <VisitsInformationTable
              fieldType={fieldType}
              value={value}
              setHasChanges={setHasChanges}
              updateContext={updateContext}
              setSaveSOE={setSaveSOE}
              setTableVisibility={setTableVisibility}
              tableVisibility={tableVisibility}
            />
          )}
          {value === 0 && showInitialRiskAssessment && (
            <InitialRiskAssessmentTable />
          )}
          {value === 0 && showVisitSampleStrategy && (
            <VisitSampleStrategyTables
              editOpen={editOpen}
              setEditOpen={setEditOpen}
            />
          )}
        </TabContainer>
      )
  );
}

const TabContainer = styled.div`
  grid-gap: 2em;
  display: grid;
`;